import { OnlineStorage, DatasetType, FileDataset, FilePack, DatasetBase } from "@tblabs/storage";
import { Snack, Span, Line, Button, JsonViewer, RefNumber, RefString, NumberInput, MultilineInput } from "@tblabs/truffle";
import { Bold } from "../Components/Bold";
import { DangerButton } from "../Components/DangerButton";
import { Panel } from "../Components/Panel";
import { PrimaryButton } from "../Components/PrimaryButton";
import { Page } from "./Page";


export class DatasetItemViewPage extends Page
{
    constructor(private _storage: OnlineStorage, private datasetName: string, private itemId: string)
    {
        super(`Item ${itemId} view`);

        this.Content(async () =>
        {
            const metadata = await this._storage.GetDatasetMetadata(this.datasetName);

            switch (metadata.Type)
            {
                case DatasetType.Object:
                    await this.ObjectItemViewRoutine();
                    break;
                case DatasetType.Text:
                    await this.TextItemViewRoutine();
                    break;
                case DatasetType.Picture:
                    await this.PictureItemViewRoutine();
                    break;
                case DatasetType.File:
                    await this.FileItemViewRoutine();
                    break;
                default:
                    new Snack("Unknown dataset type. Can not view item.");
                    break;
            }
        });
    }

    private async ObjectItemViewRoutine()
    {
        const dataset = this._storage.GetObjectDataset(this.datasetName);
        const item = await dataset.GetOne(this.itemId);
        const dir = this._storage.Server + "object/" + this._storage.DatabaseDir + "/" + this.datasetName + "/" + this.itemId;

        this.body.Append(
            new Panel(["Item ", new Span(this.itemId).Bold()], [
                "Of ", new Span(DatasetType.Object).Bold(), " type in ", new Span(this.datasetName).Bold(), " dataset",
                new Line(),
                new Button("Link", () => window.open(dir, "_blank")),
                new PrimaryButton("Edit", () => window.location.hash = `admin/dataset/${DatasetType.Object}/${this.datasetName}/${this.itemId}/edit`).MarginLeft(12),
                new DangerButton("Delete", () => this.DeleteItem(dataset, this.itemId)).MarginLeft(12),
            ]),
            new Panel("Content", [
                new JsonViewer(item, undefined, {
                    keyStyle: "color: #d2d2d2;",
                    stringStyle: "color: #fdfdfd;",
                    numberStyle: "color: #fdfdfd;",
                    boolStyle: "color: #fdfdfd;",
                    nullStyle: "color: #fdfdfd;",
                    foldAll: false
                }),
            ])
        );
    }

    private async TextItemViewRoutine()
    {
        const offset = new RefNumber(0);
        const limit = new RefNumber(100);
        const text = new RefString("");

        const dataset = this._storage.GetTextDataset(this.datasetName);

        const Read = async () =>
        {
            const lines = await dataset.Read(this.itemId, offset.value, limit.value);
            text.value = lines.join("\n");
        };

        const dir = this._storage.Server + "text/" + this._storage.DatabaseDir + "/" + this.datasetName + "/" + this.itemId;

        this.body.Append(
            new Panel(["Item ", new Bold(this.itemId)], [
                "Of ", new Bold(DatasetType.Text), " type in ", new Bold(this.datasetName), " dataset",
                new Line(),
                new Button("Link", () => window.open(dir, "_blank")),
                new PrimaryButton("Edit", () => window.location.hash = `admin/dataset/Text/${this.datasetName}/${this.itemId}/edit`).MarginLeft(12),
                new DangerButton("Delete", () => this.DeleteItem(dataset, this.itemId)).MarginLeft(12),
            ]),
            new Panel("Content", [
                "Offset: ", new NumberInput(offset),
                " Limit: ", new NumberInput(limit).Min(1),
                new Button("Refresh", async () => await Read()).MarginLeft(16),
                new MultilineInput(text).Rows(20).Readonly().MarginTop(16),
            ])
        );

        await Read();
    }

    private async PictureItemViewRoutine()
    {
        const dataset = this._storage.GetPictureDataset(this.datasetName);
        const item = await dataset.GetOne(this.itemId);

        this.body.Append(
            new Panel(["Item ", new Bold(this.itemId)], [
                new Bold(DatasetType.Picture).Bold(), " in ", new Bold(this.datasetName).Bold(), " dataset",
                new Line(),
                new DangerButton("Delete", () => this.DeleteItem(dataset, this.itemId)),
                new Span("This type of item can not be edited").Italic().MarginLeft(16),
            ]),
            new Panel("Content", [
                new JsonViewer(item, undefined, {
                    keyStyle: "color: #d2d2d2;",
                    stringStyle: "color: #fdfdfd;",
                    numberStyle: "color: #fdfdfd;",
                    boolStyle: "color: #fdfdfd;",
                    nullStyle: "color: #fdfdfd;",
                    foldAll: false
                })
            ])
        );
    }

    private async FileItemViewRoutine()
    {
        const dataset: FileDataset = this._storage.GetFileDataset(this.datasetName);
        const item: FilePack | undefined = await dataset.GetOne(this.itemId);

        this.body.Append(
            new Panel(["Item ", new Span(this.itemId).Bold()], [
                new Span(DatasetType.File).Bold(), " in ", new Span(this.datasetName).Bold(), " dataset",
                new Line(),
                new DangerButton("Delete", () => this.DeleteItem(dataset, this.itemId)),
                new Span("This type of item can not be edited").Italic().MarginLeft(16),
            ]),
            new Panel("Content", [
                new JsonViewer(item, undefined, {
                    keyStyle: "color: #d2d2d2;",
                    stringStyle: "color: #fdfdfd;",
                    numberStyle: "color: #fdfdfd;",
                    boolStyle: "color: #fdfdfd;",
                    nullStyle: "color: #fdfdfd;",
                    foldAll: false
                })
            ])
        );
    }

    private async DeleteItem(dataset: DatasetBase, id: string): Promise<void>
    {
        try
        {
            if (confirm("Are you sure you want to delete this item?"))
            {
                await dataset.Delete(id);

                new Snack("✅ Item deleted");

                window.location.hash = `admin/database`;
            }
        }
        catch (ex: any)
        {
            new Snack("❌ Failed to delete item", ex.message);
        }
    }
}
